import { useCallback, useEffect, useMemo, useState } from "react";
import {
  adminLogin,
  adminLogout,
  adminMoveProject,
  adminSetPaused,
  adminStatus,
  adminVisits,
  adminInterests,
  adminInterestsCsv,
  adminRefreshDelivery,
  getAdminToken,
  setAdminToken,
} from "../lib/api";
import axios from "axios";

// Commons Admin v1. Three levers: password login, Overflow move,
// Pause. Deliberately utilitarian. Not wrapped in <Gate> — Admin
// must be reachable even when Commons is paused and even before the
// visitor's onboarding provisioning has completed.

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

export default function Admin() {
  const [status, setStatus] = useState(null);
  const [projects, setProjects] = useState(null);
  const [password, setPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [pauseConfirm, setPauseConfirm] = useState(false);

  const refreshStatus = useCallback(async () => {
    const s = await adminStatus();
    setStatus(s);
    return s;
  }, []);

  const refreshProjects = useCallback(async () => {
    // Admin needs the canonical project list (from any session — the
    // seed is authoritative for identification here). We don't have
    // an admin-scoped project list endpoint; borrow the visitor
    // /commons response, which now includes libraryPlacement.
    const r = await axios.get(`${API}/commons`, {
      headers: {
        "X-Session-Id": "admin-panel-view-session",
        "X-Admin-Token": getAdminToken() || "",
      },
    });
    setProjects(r.data.projects || []);
  }, []);

  useEffect(() => {
    (async () => {
      const s = await refreshStatus();
      if (s.authorized) await refreshProjects();
    })();
  }, [refreshStatus, refreshProjects]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoginErr("");
    setBusy(true);
    try {
      const r = await adminLogin(password);
      setAdminToken(r.token);
      setPassword("");
      const s = await refreshStatus();
      if (s.authorized) await refreshProjects();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      setLoginErr(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const onLogout = async () => {
    await adminLogout();
    setStatus(null);
    setProjects(null);
    await refreshStatus();
  };

  const onTogglePause = async () => {
    if (!status?.paused) {
      setPauseConfirm(true);
      return;
    }
    setBusy(true);
    try {
      await adminSetPaused(false);
      await refreshStatus();
    } finally {
      setBusy(false);
    }
  };

  const confirmPause = async () => {
    setPauseConfirm(false);
    setBusy(true);
    try {
      await adminSetPaused(true);
      await refreshStatus();
    } finally {
      setBusy(false);
    }
  };

  const onMove = async (projectId, to) => {
    setBusy(true);
    try {
      await adminMoveProject(projectId, to);
      await refreshStatus();
      await refreshProjects();
    } finally {
      setBusy(false);
    }
  };

  // Not-authenticated view — password screen.
  if (!status || !status.authorized) {
    return (
      <div className="c-admin-page" data-testid="admin-page">
        <div className="c-admin-shell">
          <div className="c-admin-title">COMMONS ADMIN</div>
          <form className="c-admin-login" onSubmit={onLogin} data-testid="admin-login-form">
            <label className="c-admin-label" htmlFor="admin-pw">Password</label>
            <input
              id="admin-pw"
              type="password"
              autoFocus
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="c-admin-input"
              data-testid="admin-password-input"
              disabled={busy}
            />
            {loginErr ? (
              <div className="c-admin-error" data-testid="admin-login-error">{loginErr}</div>
            ) : null}
            <button
              type="submit"
              className="c-btn c-btn-primary c-admin-submit"
              disabled={busy || !password}
              data-testid="admin-login-submit"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
            {status && !status.adminPasswordConfigured ? (
              <div className="c-admin-hint" data-testid="admin-pw-not-configured">
                Admin password is not configured. Ask your operator.
              </div>
            ) : null}
          </form>
        </div>
      </div>
    );
  }

  // Authenticated view.
  return (
    <div className="c-admin-page" data-testid="admin-page">
      <div className="c-admin-shell">
        <div className="c-admin-topbar">
          <div className="c-admin-title">COMMONS ADMIN</div>
          <button className="c-admin-logout" onClick={onLogout} data-testid="admin-logout">Log out</button>
        </div>

        <SectionHeader label="SITE" />
        <div className="c-admin-site" data-testid="admin-site-block">
          {status.paused ? (
            <>
              <div className="c-admin-status c-admin-status-paused" data-testid="admin-site-status">
                <span aria-hidden>⏸</span>
                <span>Commons is paused</span>
              </div>
              <button
                className="c-btn c-btn-primary c-admin-siteaction"
                onClick={onTogglePause}
                disabled={busy}
                data-testid="admin-activate-btn"
              >
                Activate Commons
              </button>
            </>
          ) : (
            <>
              <div className="c-admin-status c-admin-status-active" data-testid="admin-site-status">
                <span aria-hidden>🟢</span>
                <span>Commons is active</span>
              </div>
              <button
                className="c-btn c-btn-inverse c-admin-siteaction"
                onClick={onTogglePause}
                disabled={busy}
                data-testid="admin-pause-btn"
              >
                Pause Commons
              </button>
            </>
          )}
        </div>

        <SectionHeader label="PROJECT LIBRARY" />
        <LibraryList
          projects={projects}
          overflowIds={status.overflowProjectIds || []}
          onMove={onMove}
          busy={busy}
        />

        <SectionHeader label="VISITORS" />
        <VisitorsPanel />

        <SectionHeader label="GET INVOLVED SUBMISSIONS" />
        <InterestsPanel />
      </div>

      {pauseConfirm ? (
        <ConfirmModal
          title="Pause Commons for everyone?"
          confirmLabel="Pause Commons"
          onConfirm={confirmPause}
          onCancel={() => setPauseConfirm(false)}
          testId="admin-pause-confirm"
        />
      ) : null}
    </div>
  );
}

function SectionHeader({ label }) {
  return <div className="c-admin-section-header">{label}</div>;
}

function LibraryList({ projects, overflowIds, onMove, busy }) {
  const overflowSet = useMemo(() => new Set(overflowIds), [overflowIds]);
  const { main, overflow } = useMemo(() => {
    const main = [];
    const overflow = [];
    (projects || []).forEach((p) => {
      // Draft-in-session projects belong to a specific visitor; the
      // admin overlay only makes sense for seed projects. The
      // backend enforces that /admin/library/move only accepts seed
      // ids, so skip session-only entries here too.
      if (p.status === "draft" && !p.stewardId) return;
      // Past attempts live inside their chain's row; the current attempt decides placement.
      if (p.nextAttemptId) return;
      if (overflowSet.has(p.id) || p.libraryPlacement === "overflow") overflow.push(p);
      else main.push(p);
    });
    const byTitle = (a, b) => (a.title || "").localeCompare(b.title || "");
    main.sort(byTitle);
    overflow.sort(byTitle);
    return { main, overflow };
  }, [projects, overflowSet]);

  if (!projects) {
    return <div className="c-admin-empty">Loading projects…</div>;
  }

  return (
    <div className="c-admin-library">
      <div className="c-admin-library-heading" data-testid="admin-main-heading">
        Main Library <span className="c-admin-count">({main.length})</span>
      </div>
      <div className="c-admin-rows" data-testid="admin-main-rows">
        {main.map((p) => (
          <ProjectRow
            key={p.id}
            project={p}
            actionLabel="Move to Overflow"
            onClick={() => onMove(p.id, "overflow")}
            busy={busy}
            testId={`admin-row-main-${p.id}`}
            note={historyNote(p, projects, overflowSet)}
          />
        ))}
      </div>

      <div className="c-admin-library-heading c-admin-library-heading-2" data-testid="admin-overflow-heading">
        Overflow <span className="c-admin-count">({overflow.length})</span>
      </div>
      <div className="c-admin-rows" data-testid="admin-overflow-rows">
        {overflow.length === 0 ? (
          <div className="c-admin-empty">No projects in Overflow.</div>
        ) : (
          overflow.map((p) => (
            <ProjectRow
              key={p.id}
              project={p}
              actionLabel="Return to Library"
              onClick={() => onMove(p.id, "main")}
              busy={busy}
              testId={`admin-row-overflow-${p.id}`}
              note={historyNote(p, projects, overflowSet)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function historyNote(project, projects, overflowSet) {
  const byId = new Map(projects.map((p) => [p.id, p]));
  const past = [];
  for (let cur = byId.get(project.previousAttemptId); cur && !past.includes(cur); cur = byId.get(cur.previousAttemptId)) past.push(cur);
  if (!past.length) return null;
  const stale = past.filter((p) => overflowSet.has(p.id)).length;
  return `Includes ${past.length} past attempt${past.length === 1 ? "" : "s"}`
    + (stale ? ` · ${stale} also listed in Overflow (no effect)` : "");
}

function ProjectRow({ project, actionLabel, onClick, busy, testId, note }) {
  return (
    <div className="c-admin-row" data-testid={testId}>
      <div className="c-admin-row-emoji" aria-hidden>{project.emoji || "•"}</div>
      <div className="c-admin-row-info">
        <div className="c-admin-row-title">{project.title || project.id}</div>
        <div className="c-admin-row-status">{project.status || "—"}</div>
        {note ? <div className="c-admin-row-status" data-testid={`${testId}-history`}>{note}</div> : null}
      </div>
      <button
        className="c-btn c-btn-ghost c-admin-row-action"
        onClick={onClick}
        disabled={busy}
        data-testid={`${testId}-action`}
      >
        {actionLabel}
      </button>
    </div>
  );
}

function ConfirmModal({ title, confirmLabel, onConfirm, onCancel, testId }) {
  return (
    <div className="c-admin-modal-scrim" onClick={onCancel} data-testid={testId}>
      <div className="c-admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="c-admin-modal-title">{title}</div>
        <div className="c-admin-modal-actions">
          <button className="c-btn c-btn-ghost" onClick={onCancel} data-testid={`${testId}-cancel`}>
            Cancel
          </button>
          <button className="c-btn c-btn-primary" onClick={onConfirm} data-testid={`${testId}-confirm`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}


// ----- Visitors panel (Feb 2026) ---------------------------------
// Operator-only view of visitor analytics. One tiny read on mount;
// no polling, no controls beyond the initial fetch. Design
// deliberately quiet — same section header pattern as the Library
// list above, no charts. Reads from /admin/visits, which is
// admin-authenticated.

function VisitorsPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        setData(await adminVisits());
      } catch (e) {
        setErr(String(e?.message || e));
      }
    })();
  }, []);
  if (err) {
    return (
      <div className="c-admin-empty" data-testid="admin-visitors-error">
        Couldn't load visitor counts.
      </div>
    );
  }
  if (!data) {
    return <div className="c-admin-empty" data-testid="admin-visitors-loading">Loading…</div>;
  }
  return (
    <div className="c-admin-visitors" data-testid="admin-visitors">
      <div className="c-admin-visitors-total" data-testid="admin-visitors-total">
        <div className="c-admin-visitors-total-label">Total visitors</div>
        <div className="c-admin-visitors-total-value">{data.total.toLocaleString()}</div>
      </div>
      <div className="c-admin-visitors-daily-heading">
        Daily visitors
      </div>
      {data.daily.length === 0 ? (
        <div className="c-admin-empty" data-testid="admin-visitors-daily-empty">
          No visits recorded yet.
        </div>
      ) : (
        <div className="c-admin-visitors-daily" data-testid="admin-visitors-daily">
          {data.daily.map((row) => (
            <div
              key={row.date}
              className="c-admin-visitors-day"
              data-testid={`admin-visitors-day-${row.date}`}
            >
              <span className="c-admin-visitors-day-date">{row.date}</span>
              <span className="c-admin-visitors-day-count">{row.count.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ----- Get Involved submissions ----------------------------------
const ACK_MODE_LABELS = {
  off: "Off",
  test: "Test only (Resend test inboxes and listed test addresses)",
  live: "Live",
};
const fmtDate = (iso) =>
  new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

function InterestsPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkMsg, setCheckMsg] = useState("");
  const load = () => adminInterests().then(setData).catch(() => setErr(true));
  useEffect(() => {
    load();
  }, []);
  const onRefreshDelivery = async () => {
    setChecking(true);
    setCheckMsg("");
    try {
      const res = await adminRefreshDelivery();
      setCheckMsg(res.error || `Delivery status checked (${res.updated} updated).`);
      await load();
    } catch {
      setCheckMsg("Delivery check failed. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const [exportErr, setExportErr] = useState(false);
  const onExport = async () => {
    setExportErr(false);
    try {
      const blob = await adminInterestsCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `commons-interest-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setExportErr(true);
    }
  };

  if (err) return <div className="c-admin-empty" data-testid="admin-interests-error">Couldn't load submissions.</div>;
  if (!data) return <div className="c-admin-empty" data-testid="admin-interests-loading">Loading…</div>;
  return (
    <div className="c-admin-interests" data-testid="admin-interests">
      <div className="c-admin-interests-bar">
        <span data-testid="admin-interests-total">{data.total} submission{data.total === 1 ? "" : "s"}</span>
        <span className="c-admin-interests-actions">
          <button className="c-btn c-btn-ghost c-admin-row-action" onClick={onRefreshDelivery}
            disabled={checking} data-testid="admin-interests-refresh-delivery-btn">
            {checking ? "Checking…" : "Check delivery"}
          </button>
          <button className="c-btn c-btn-ghost c-admin-row-action" onClick={onExport}
            disabled={data.total === 0} data-testid="admin-interests-export-btn">Export CSV</button>
        </span>
      </div>
      <div className="c-admin-interests-mode" data-testid="admin-interests-ack-mode">
        Acknowledgment emails: <strong>{ACK_MODE_LABELS[data.ackMode] || data.ackMode}</strong>
      </div>
      {checkMsg ? <div className="c-admin-interests-mode" data-testid="admin-interests-check-msg">{checkMsg}</div> : null}
      {exportErr ? <div className="c-admin-error" data-testid="admin-interests-export-error">Export failed. Please try again.</div> : null}
      {data.items.length === 0 ? (
        <div className="c-admin-empty" data-testid="admin-interests-empty">No submissions yet.</div>
      ) : (
        data.items.map((it) => (
          <div key={it.id} className="c-admin-interest" data-testid={`admin-interest-${it.id}`}>
            <div className="c-admin-interest-head">
              <span className="c-admin-interest-email">{it.email}</span>
              <span className="c-admin-interest-date">{fmtDate(it.createdAt)}</span>
            </div>
            {it.name ? <div className="c-admin-interest-name">{it.name}</div> : null}
            {it.interests?.length ? (
              <div className="c-admin-interest-tags">
                {it.interests.map((t) => <span key={t} className="c-admin-interest-tag">{t}</span>)}
              </div>
            ) : null}
            {it.message ? <div className="c-admin-interest-msg">{it.message}</div> : null}
            <div className={`c-admin-interest-ack is-${it.ackTone}`} data-testid={`admin-interest-ack-${it.id}`}>
              <span className="c-admin-interest-ack-label">{it.ackLabel}</span>
              {it.ackDetail ? <span className="c-admin-interest-ack-detail"> · {it.ackDetail}</span> : null}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
